import React, { useState } from 'react';
import { X } from 'lucide-react';

import Button from './Button';
import { trackCatalogDownload, trackFormSubmission } from 'utils/analytics';
import Icon from '../AppIcon';


const CatalogDownloadModal = ({ isOpen, onClose, catalogUrl }) => {
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.nom?.trim()) {
      newErrors.nom = 'Le nom est requis';
    }

    if (!formData?.prenom?.trim()) {
      newErrors.prenom = 'Le prénom est requis';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Email invalide';
    }

    if (!formData?.telephone?.trim()) {
      newErrors.telephone = 'Le numéro de téléphone est requis';
    } else if (!/^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/?.test(formData?.telephone)) {
      newErrors.telephone = 'Numéro de téléphone invalide';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault();

    if (validateForm()) {
      // Track form submission
      trackFormSubmission('catalog_download_form', {
        catalog_url: catalogUrl,
        user_name: formData?.nom,
        user_email: formData?.email,
        user_phone: formData?.telephone,
        user_company: formData?.entreprise
      });

      // Track catalog download event
      trackCatalogDownload('professional_training_catalog', {
        email: formData?.email,
        company: formData?.entreprise,
        name: formData?.nom,
        phone: formData?.telephone
      });

      console.log('Form submitted:', formData);
      
      // Simulate API call
      setTimeout(() => {
        setIsSubmitting(false);
        
        // Open Google Drive link in new tab with proper parameters
        const newWindow = window.open(catalogUrl, '_blank', 'noopener,noreferrer');
        
        // Fallback if popup blocked
        if (!newWindow || newWindow?.closed || typeof newWindow?.closed === 'undefined') {
          // If popup blocked, create a temporary link and click it
          const link = document.createElement('a');
          link.href = catalogUrl;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          document.body?.appendChild(link);
          link?.click();
          document.body?.removeChild(link);
        }
        
        // Reset form and close modal
        setFormData({
          nom: '',
          prenom: '',
          email: '',
          telephone: ''
        });
        onClose();
      }, 500);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Fermer"
        >
          <Icon name="X" size={24} />
        </button>

        {/* Modal Content */}
        <div className="p-8">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                 style={{ backgroundColor: 'rgba(155, 142, 87, 0.1)' }}>
              <Icon name="Download" size={32} style={{ color: '#9B8E57' }} />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Télécharger le Catalogue
            </h2>
            <p className="text-gray-600">
              Veuillez remplir ce formulaire pour accéder au catalogue de formations
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Nom */}
            <div>
              <label htmlFor="nom" className="block text-sm font-semibold text-gray-700 mb-2">
                Nom <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="nom"
                name="nom"
                value={formData?.nom}
                onChange={handleChange}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                  errors?.nom ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-[#9B8E57]'
                }`}
                placeholder="Votre nom"
              />
              {errors?.nom && (
                <p className="mt-1 text-sm text-red-500 flex items-center">
                  <Icon name="AlertCircle" size={14} className="mr-1" />
                  {errors?.nom}
                </p>
              )}
            </div>

            {/* Prénom */}
            <div>
              <label htmlFor="prenom" className="block text-sm font-semibold text-gray-700 mb-2">
                Prénom <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="prenom"
                name="prenom"
                value={formData?.prenom}
                onChange={handleChange}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                  errors?.prenom ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-[#9B8E57]'
                }`}
                placeholder="Votre prénom"
              />
              {errors?.prenom && (
                <p className="mt-1 text-sm text-red-500 flex items-center">
                  <Icon name="AlertCircle" size={14} className="mr-1" />
                  {errors?.prenom}
                </p>
              )}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData?.email}
                onChange={handleChange}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                  errors?.email ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-[#9B8E57]'
                }`}
                placeholder="votre.email@exemple.com"
              />
              {errors?.email && (
                <p className="mt-1 text-sm text-red-500 flex items-center">
                  <Icon name="AlertCircle" size={14} className="mr-1" />
                  {errors?.email}
                </p>
              )}
            </div>

            {/* Téléphone */}
            <div>
              <label htmlFor="telephone" className="block text-sm font-semibold text-gray-700 mb-2">
                Numéro de téléphone <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                id="telephone"
                name="telephone"
                value={formData?.telephone}
                onChange={handleChange}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                  errors?.telephone ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-[#9B8E57]'
                }`}
                placeholder="+1 234 567 8900"
              />
              {errors?.telephone && (
                <p className="mt-1 text-sm text-red-500 flex items-center">
                  <Icon name="AlertCircle" size={14} className="mr-1" />
                  {errors?.telephone}
                </p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-6 py-4 text-white font-semibold rounded-lg transition-all duration-300 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: '#96121D' }}
              onMouseEnter={(e) => {
                if (!isSubmitting) {
                  e.target.style.backgroundColor = '#7d0f18';
                  e.target.style.transform = 'translateY(-2px)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isSubmitting) {
                  e.target.style.backgroundColor = '#96121D';
                  e.target.style.transform = 'translateY(0)';
                }
              }}
            >
              {isSubmitting ? (
                <>
                  <Icon name="Loader" size={20} className="mr-2 animate-spin" />
                  Traitement...
                </>
              ) : (
                <>
                  Accéder au Catalogue
                  <Icon name="ArrowRight" size={20} className="ml-2" />
                </>
              )}
            </button>
          </form>

          {/* Privacy Note */}
          <p className="mt-4 text-xs text-gray-500 text-center">
            Vos informations sont sécurisées et ne seront utilisées que pour vous contacter concernant nos formations.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CatalogDownloadModal;